import * as XLSX from 'xlsx';

/**
 * Variable Key Generator for Curve Features Export
 * Generates organized variable key sheets matching the structure from report_guide.py
 */

/**
 * Organized feature categories with their descriptions
 * Based on report_guide.py structure
 */
const featureCategories = {
  'Region Definitions': {
    '_CURVE': 'The main TAC curve region where the alcohol concentration rises and falls',
    '_PERIPHERY': 'The region before and after the curve, used for quality assessment',
    '_PERIPHERY_BEFORE': 'The region before the curve start, used for quality assessment',
    '_PERIPHERY_AFTER': 'The region after the curve end, used for quality assessment',
    '_REGION': 'The region consisting of CURVE + PERIPHERY',
    '_EMA_REGION': 'The 12-hour region starting at the earliest timestamp of the event'
  },
  
  'Basic Identifiers': {
    'subid': 'Subject identifier',
    'dataset_id': 'Dataset identifier',
    'curve_id': 'Curve identifier',
    'curve_count': 'Number of curves for this subject',
    'curve_threshold': 'Threshold used for curve detection',
    'device_one_REGION': 'First device ID in the region',
    'device_two_REGION': 'Second device ID in the region',
    'device_count_REGION': 'Number of devices in the region'
  },
  
  'TAC Curve Features': {
    'duration_CURVE': 'Total duration of the curve (hours)',
    'begin_CURVE': 'Start time of the curve',
    'end_CURVE': 'End time of the curve',
    'auc_total_CURVE': 'Area under the curve (total) (µg/L/hour)',
    'auc_relative_CURVE': 'Area under the curve (relative to baseline) (µg/L/hour)',
    'peak_CURVE': 'Maximum TAC value (µg/L)',
    'relative_peak_CURVE': 'Maximum TAC value relative to baseline (µg/L)',
    'first_tac_CURVE': 'First TAC value in the curve',
    'last_tac_CURVE': 'Last TAC value in the curve',
    'mean_tac_CURVE': 'Mean TAC value in the curve',
    'sd_tac_CURVE': 'Standard deviation of TAC values in the curve',
    'sem_tac_CURVE': 'Standard error of the mean TAC values in the curve'
  },
  
  'Rise Phase Features': {
    'rise_duration_CURVE': 'Duration of TAC increase (hours)',
    'rise_rate_CURVE': 'Rate of TAC increase (Peak / Rise Duration) (µg/(L·h))',
    'rise_rate_point_to_point_CURVE': 'Point-to-point rise rate',
    'rise_duration_point_to_point_CURVE': 'Point-to-point rise duration',
    'rise_rate_1hr_CURVE': '1-hour rise rate',
    'rise_rate_2hr_CURVE': '2-hour rise rate',
    'rise_complete_percent_CURVE': 'Percentage of rise phase that is complete (peak - first_tac)/relative_peak',
    'rise_imputed_percent_CURVE': 'Percentage of imputed data during the rise phase',
    'ascending_imputed_percent_CURVE': 'Percentage of imputed data during ascending portions'
  },
  
  'Fall Phase Features': {
    'fall_duration_CURVE': 'Duration of TAC decrease (hours)',
    'fall_rate_CURVE': 'Rate of TAC decrease (Peak / Fall Duration) (µg/(L·h))',
    'fall_rate_point_to_point_CURVE': 'Point-to-point fall rate',
    'fall_duration_point_to_point_CURVE': 'Point-to-point fall duration',
    'fall_rate_1hr_CURVE': '1-hour fall rate',
    'fall_rate_2hr_CURVE': '2-hour fall rate',
    'fall_complete_percent_CURVE': 'Percentage of fall phase that is complete (peak - last_tac)/relative_peak',
    'fall_imputed_percent_CURVE': 'Percentage of imputed data during the fall phase',
    'descending_imputed_percent_CURVE': 'Percentage of imputed data during descending portions',
    'rise_fall_rate_CURVE': 'Combined rise/fall rate (relative_peak / duration)',
    'get_start_to_peak_interval_CURVE': 'Interval from curve start to peak (minutes)'
  },
  
  'Curve Quality Features': {
    'total_duration_CURVE': 'Total duration of the curve region (hours)',
    'device_turned_on_duration_CURVE': 'Duration device was turned on (hours)',
    'device_turned_on_percent_CURVE': 'Percentage of time device was turned on',
    'device_worn_duration_CURVE': 'Duration device was worn (hours)',
    'device_worn_percent_CURVE': 'Percentage of time device was worn',
    'flatline_max_CURVE': 'Maximum duration of flatline (minutes)',
    'flatlined_percent_CURVE': 'Percentage of flatlined data',
    'high_quality_duration_CURVE': 'Duration of high quality data (hours)',
    'high_quality_percent_CURVE': 'Percentage of high quality data',
    'high_quality_above_threshold_duration_CURVE': 'Duration of high quality data above threshold (hours)',
    'below_threshold_percent_CURVE': 'Percentage of curve values below the detection threshold',
    'started_curve_count_CURVE': 'Number of discrete curves within the final curve',
    'complete_curve_count_CURVE': 'Number of discrete curves started and ended within the final curve'
  },
  
  'Curve Imputation Features': {
    'imputed_duration_CURVE': 'Duration of imputed data (hours)',
    'imputed_percent_CURVE': 'Percentage of data that was imputed',
    'imputed_low_quality_duration_CURVE': 'Duration of imputed low quality data (hours)',
    'imputed_low_quality_percent_CURVE': 'Percentage of imputed low quality data',
    'unimputed_low_quality_duration_CURVE': 'Duration of low quality data not imputed (hours)',
    'unimputed_low_quality_percent_CURVE': 'Percentage of low quality data not imputed',
    'total_low_quality_duration_CURVE': 'Total duration of low quality data (hours)',
    'total_low_quality_percent_CURVE': 'Total percentage of low quality data',
    'low_quality_imputation_ratio_CURVE': 'Ratio of overall low quality data that has been imputed (0-1)'
  },
  
  'Curve Gap Features': {
    'imputed_gap_duration_CURVE': 'Duration of imputed gap data (hours)',
    'imputed_gap_percent_CURVE': 'Percentage of imputed gap data',
    'unimputed_gap_duration_CURVE': 'Duration of unimputed gap data (hours)',
    'unimputed_gap_percent_CURVE': 'Percentage of unimputed gap data',
    'total_gap_duration_CURVE': 'Total duration of gap data (hours)',
    'total_gap_percent_CURVE': 'Total percentage of gap data',
    'gap_imputation_ratio_CURVE': 'Ratio of gap data that has been imputed (0-1)'
  },
  
  'Curve Non-Wear Features': {
    'imputed_non_wear_duration_CURVE': 'Duration of imputed non-wear data (hours)',
    'imputed_non_wear_percent_CURVE': 'Percentage of imputed non-wear data',
    'unimputed_non_wear_duration_CURVE': 'Duration of unimputed non-wear data (hours)',
    'unimputed_non_wear_percent_CURVE': 'Percentage of unimputed non-wear data',
    'total_non_wear_duration_CURVE': 'Total duration of non-wear data (hours)',
    'total_non_wear_percent_CURVE': 'Total percentage of non-wear data',
    'non_wear_imputation_ratio_CURVE': 'Ratio of non-wear data that has been imputed (0-1)',
    'total_gaps_and_non_wear_percent_CURVE': 'Total percentage of gaps and non-wear combined',
    'unimputed_gaps_and_non_wear_percent_CURVE': 'Percentage of unimputed gaps and non-wear combined'
  },
  
  'Curve Artifact Features': {
    'imputed_jump_duration_CURVE': 'Duration of imputed jump data (hours)',
    'imputed_jump_percent_CURVE': 'Percentage of imputed jump data',
    'unimputed_jump_duration_CURVE': 'Duration of unimputed jump data (hours)',
    'unimputed_jump_percent_CURVE': 'Percentage of unimputed jump data',
    'total_jump_duration_CURVE': 'Total duration of jump data (hours)',
    'total_jump_percent_CURVE': 'Total percentage of jump data',
    'jump_imputation_ratio_CURVE': 'Ratio of jump data that has been imputed (0-1)',
    'imputed_plummet_duration_CURVE': 'Duration of imputed plummet data (hours)',
    'imputed_plummet_percent_CURVE': 'Percentage of imputed plummet data',
    'unimputed_plummet_duration_CURVE': 'Duration of unimputed plummet data (hours)',
    'unimputed_plummet_percent_CURVE': 'Percentage of unimputed plummet data',
    'total_plummet_duration_CURVE': 'Total duration of plummet data (hours)',
    'total_plummet_percent_CURVE': 'Total percentage of plummet data',
    'plummet_imputation_ratio_CURVE': 'Ratio of plummet data that has been imputed (0-1)',
    'imputed_extreme_negative_duration_CURVE': 'Duration of imputed extreme negative data (hours)',
    'imputed_extreme_negative_percent_CURVE': 'Percentage of imputed extreme negative data',
    'unimputed_extreme_negative_duration_CURVE': 'Duration of unimputed extreme negative data (hours)',
    'unimputed_extreme_negative_percent_CURVE': 'Percentage of unimputed extreme negative data',
    'total_extreme_negative_duration_CURVE': 'Total duration of extreme negative data (hours)',
    'total_extreme_negative_percent_CURVE': 'Total percentage of extreme negative data',
    'sub_negative_10_sum_CURVE': 'Sum of all TAC values below -10',
    'extreme_negative_imputation_ratio_CURVE': 'Ratio of extreme negative data that has been imputed (0-1)'
  },
  
  'Periphery Before Quality Features': {
    'total_duration_PERIPHERY_BEFORE': 'Total duration of the periphery before the curve (hours)',
    'device_turned_on_duration_PERIPHERY_BEFORE': 'Duration device was turned on in periphery before (hours)',
    'device_turned_on_percent_PERIPHERY_BEFORE': 'Percentage of time device was turned on in periphery before',
    'device_worn_duration_PERIPHERY_BEFORE': 'Duration device was worn in periphery before (hours)',
    'device_worn_percent_PERIPHERY_BEFORE': 'Percentage of time device was worn in periphery before',
    'imputed_duration_PERIPHERY_BEFORE': 'Duration of imputed data in periphery before (hours)',
    'imputed_percent_PERIPHERY_BEFORE': 'Percentage of imputed data in periphery before',
    'imputed_low_quality_duration_PERIPHERY_BEFORE': 'Duration of imputed low quality data in periphery before (hours)',
    'imputed_low_quality_percent_PERIPHERY_BEFORE': 'Percentage of imputed low quality data in periphery before',
    'unimputed_low_quality_duration_PERIPHERY_BEFORE': 'Duration of low quality data not imputed in periphery before (hours)',
    'unimputed_low_quality_percent_PERIPHERY_BEFORE': 'Percentage of low quality data not imputed in periphery before',
    'total_low_quality_duration_PERIPHERY_BEFORE': 'Total duration of low quality data in periphery before (hours)',
    'total_low_quality_percent_PERIPHERY_BEFORE': 'Total percentage of low quality data in periphery before',
    'imputed_gap_duration_PERIPHERY_BEFORE': 'Duration of imputed gap data in periphery before (hours)',
    'imputed_gap_percent_PERIPHERY_BEFORE': 'Percentage of imputed gap data in periphery before',
    'unimputed_gap_duration_PERIPHERY_BEFORE': 'Duration of unimputed gap data in periphery before (hours)',
    'unimputed_gap_percent_PERIPHERY_BEFORE': 'Percentage of unimputed gap data in periphery before',
    'total_gap_duration_PERIPHERY_BEFORE': 'Total duration of gap data in periphery before (hours)',
    'total_gap_percent_PERIPHERY_BEFORE': 'Total percentage of gap data in periphery before',
    'gap_imputation_ratio_PERIPHERY_BEFORE': 'Ratio of gap data that has been imputed in periphery before (0-1)',
    'imputed_non_wear_duration_PERIPHERY_BEFORE': 'Duration of imputed non-wear data in periphery before (hours)',
    'imputed_non_wear_percent_PERIPHERY_BEFORE': 'Percentage of imputed non-wear data in periphery before',
    'unimputed_non_wear_duration_PERIPHERY_BEFORE': 'Duration of unimputed non-wear data in periphery before (hours)',
    'unimputed_non_wear_percent_PERIPHERY_BEFORE': 'Percentage of unimputed non-wear data in periphery before',
    'total_non_wear_duration_PERIPHERY_BEFORE': 'Total duration of non-wear data in periphery before (hours)',
    'total_non_wear_percent_PERIPHERY_BEFORE': 'Total percentage of non-wear data in periphery before',
    'non_wear_imputation_ratio_PERIPHERY_BEFORE': 'Ratio of non-wear data that has been imputed in periphery before (0-1)',
    'imputed_jump_duration_PERIPHERY_BEFORE': 'Duration of imputed jump data in periphery before (hours)',
    'imputed_jump_percent_PERIPHERY_BEFORE': 'Percentage of imputed jump data in periphery before',
    'unimputed_jump_duration_PERIPHERY_BEFORE': 'Duration of unimputed jump data in periphery before (hours)',
    'unimputed_jump_percent_PERIPHERY_BEFORE': 'Percentage of unimputed jump data in periphery before',
    'total_jump_duration_PERIPHERY_BEFORE': 'Total duration of jump data in periphery before (hours)',
    'total_jump_percent_PERIPHERY_BEFORE': 'Total percentage of jump data in periphery before',
    'jump_imputation_ratio_PERIPHERY_BEFORE': 'Ratio of jump data that has been imputed in periphery before (0-1)',
    'imputed_plummet_duration_PERIPHERY_BEFORE': 'Duration of imputed plummet data in periphery before (hours)',
    'imputed_plummet_percent_PERIPHERY_BEFORE': 'Percentage of imputed plummet data in periphery before',
    'unimputed_plummet_duration_PERIPHERY_BEFORE': 'Duration of unimputed plummet data in periphery before (hours)',
    'unimputed_plummet_percent_PERIPHERY_BEFORE': 'Percentage of unimputed plummet data in periphery before',
    'total_plummet_duration_PERIPHERY_BEFORE': 'Total duration of plummet data in periphery before (hours)',
    'total_plummet_percent_PERIPHERY_BEFORE': 'Total percentage of plummet data in periphery before',
    'plummet_imputation_ratio_PERIPHERY_BEFORE': 'Ratio of plummet data that has been imputed in periphery before (0-1)',
    'imputed_extreme_negative_duration_PERIPHERY_BEFORE': 'Duration of imputed extreme negative data in periphery before (hours)',
    'imputed_extreme_negative_percent_PERIPHERY_BEFORE': 'Percentage of imputed extreme negative data in periphery before',
    'unimputed_extreme_negative_duration_PERIPHERY_BEFORE': 'Duration of unimputed extreme negative data in periphery before (hours)',
    'unimputed_extreme_negative_percent_PERIPHERY_BEFORE': 'Percentage of unimputed extreme negative data in periphery before',
    'total_extreme_negative_duration_PERIPHERY_BEFORE': 'Total duration of extreme negative data in periphery before (hours)',
    'total_extreme_negative_percent_PERIPHERY_BEFORE': 'Total percentage of extreme negative data in periphery before',
    'sub_negative_10_sum_PERIPHERY_BEFORE': 'Sum of all TAC values below -10 in periphery before',
    'extreme_negative_imputation_ratio_PERIPHERY_BEFORE': 'Ratio of extreme negative data that has been imputed in periphery before (0-1)',
    'low_quality_imputation_ratio_PERIPHERY_BEFORE': 'Ratio of overall low quality data that has been imputed in periphery before (0-1)',
    'total_gaps_and_non_wear_percent_PERIPHERY_BEFORE': 'Total percentage of gaps and non-wear combined in periphery before'
  },
  
  'Periphery After Quality Features': {
    'total_duration_PERIPHERY_AFTER': 'Total duration of the periphery after the curve (hours)',
    'device_turned_on_duration_PERIPHERY_AFTER': 'Duration device was turned on in periphery after (hours)',
    'device_turned_on_percent_PERIPHERY_AFTER': 'Percentage of time device was turned on in periphery after',
    'device_worn_duration_PERIPHERY_AFTER': 'Duration device was worn in periphery after (hours)',
    'device_worn_percent_PERIPHERY_AFTER': 'Percentage of time device was worn in periphery after',
    'imputed_duration_PERIPHERY_AFTER': 'Duration of imputed data in periphery after (hours)',
    'imputed_percent_PERIPHERY_AFTER': 'Percentage of imputed data in periphery after',
    'imputed_low_quality_duration_PERIPHERY_AFTER': 'Duration of imputed low quality data in periphery after (hours)',
    'imputed_low_quality_percent_PERIPHERY_AFTER': 'Percentage of imputed low quality data in periphery after',
    'unimputed_low_quality_duration_PERIPHERY_AFTER': 'Duration of low quality data not imputed in periphery after (hours)',
    'unimputed_low_quality_percent_PERIPHERY_AFTER': 'Percentage of low quality data not imputed in periphery after',
    'total_low_quality_duration_PERIPHERY_AFTER': 'Total duration of low quality data in periphery after (hours)',
    'total_low_quality_percent_PERIPHERY_AFTER': 'Total percentage of low quality data in periphery after',
    'imputed_gap_duration_PERIPHERY_AFTER': 'Duration of imputed gap data in periphery after (hours)',
    'imputed_gap_percent_PERIPHERY_AFTER': 'Percentage of imputed gap data in periphery after',
    'unimputed_gap_duration_PERIPHERY_AFTER': 'Duration of unimputed gap data in periphery after (hours)',
    'unimputed_gap_percent_PERIPHERY_AFTER': 'Percentage of unimputed gap data in periphery after',
    'total_gap_duration_PERIPHERY_AFTER': 'Total duration of gap data in periphery after (hours)',
    'total_gap_percent_PERIPHERY_AFTER': 'Total percentage of gap data in periphery after',
    'gap_imputation_ratio_PERIPHERY_AFTER': 'Ratio of gap data that has been imputed in periphery after (0-1)',
    'imputed_non_wear_duration_PERIPHERY_AFTER': 'Duration of imputed non-wear data in periphery after (hours)',
    'imputed_non_wear_percent_PERIPHERY_AFTER': 'Percentage of imputed non-wear data in periphery after',
    'unimputed_non_wear_duration_PERIPHERY_AFTER': 'Duration of unimputed non-wear data in periphery after (hours)',
    'unimputed_non_wear_percent_PERIPHERY_AFTER': 'Percentage of unimputed non-wear data in periphery after',
    'total_non_wear_duration_PERIPHERY_AFTER': 'Total duration of non-wear data in periphery after (hours)',
    'total_non_wear_percent_PERIPHERY_AFTER': 'Total percentage of non-wear data in periphery after',
    'non_wear_imputation_ratio_PERIPHERY_AFTER': 'Ratio of non-wear data that has been imputed in periphery after (0-1)',
    'imputed_jump_duration_PERIPHERY_AFTER': 'Duration of imputed jump data in periphery after (hours)',
    'imputed_jump_percent_PERIPHERY_AFTER': 'Percentage of imputed jump data in periphery after',
    'unimputed_jump_duration_PERIPHERY_AFTER': 'Duration of unimputed jump data in periphery after (hours)',
    'unimputed_jump_percent_PERIPHERY_AFTER': 'Percentage of unimputed jump data in periphery after',
    'total_jump_duration_PERIPHERY_AFTER': 'Total duration of jump data in periphery after (hours)',
    'total_jump_percent_PERIPHERY_AFTER': 'Total percentage of jump data in periphery after',
    'jump_imputation_ratio_PERIPHERY_AFTER': 'Ratio of jump data that has been imputed in periphery after (0-1)',
    'imputed_plummet_duration_PERIPHERY_AFTER': 'Duration of imputed plummet data in periphery after (hours)',
    'imputed_plummet_percent_PERIPHERY_AFTER': 'Percentage of imputed plummet data in periphery after',
    'unimputed_plummet_duration_PERIPHERY_AFTER': 'Duration of unimputed plummet data in periphery after (hours)',
    'unimputed_plummet_percent_PERIPHERY_AFTER': 'Percentage of unimputed plummet data in periphery after',
    'total_plummet_duration_PERIPHERY_AFTER': 'Total duration of plummet data in periphery after (hours)',
    'total_plummet_percent_PERIPHERY_AFTER': 'Total percentage of plummet data in periphery after',
    'plummet_imputation_ratio_PERIPHERY_AFTER': 'Ratio of plummet data that has been imputed in periphery after (0-1)',
    'imputed_extreme_negative_duration_PERIPHERY_AFTER': 'Duration of imputed extreme negative data in periphery after (hours)',
    'imputed_extreme_negative_percent_PERIPHERY_AFTER': 'Percentage of imputed extreme negative data in periphery after',
    'unimputed_extreme_negative_duration_PERIPHERY_AFTER': 'Duration of unimputed extreme negative data in periphery after (hours)',
    'unimputed_extreme_negative_percent_PERIPHERY_AFTER': 'Percentage of unimputed extreme negative data in periphery after',
    'total_extreme_negative_duration_PERIPHERY_AFTER': 'Total duration of extreme negative data in periphery after (hours)',
    'total_extreme_negative_percent_PERIPHERY_AFTER': 'Total percentage of extreme negative data in periphery after',
    'sub_negative_10_sum_PERIPHERY_AFTER': 'Sum of all TAC values below -10 in periphery after',
    'extreme_negative_imputation_ratio_PERIPHERY_AFTER': 'Ratio of extreme negative data that has been imputed in periphery after (0-1)',
    'low_quality_imputation_ratio_PERIPHERY_AFTER': 'Ratio of overall low quality data that has been imputed in periphery after (0-1)',
    'total_gaps_and_non_wear_percent_PERIPHERY_AFTER': 'Total percentage of gaps and non-wear combined in periphery after'
  },
  
  'Region Quality Features': {
    'started_curve_count_REGION': 'Number of discrete curves within the region',
    'complete_curve_count_REGION': 'Number of discrete curves started and ended within the region',
    'total_duration_REGION': 'Total duration of the region (hours)',
    'device_turned_on_duration_REGION': 'Duration device was turned on in region (hours)',
    'device_turned_on_percent_REGION': 'Percentage of time device was turned on in region',
    'device_worn_duration_REGION': 'Duration device was worn in region (hours)',
    'device_worn_percent_REGION': 'Percentage of time device was worn in region',
    'flatline_max_REGION': 'Maximum duration of flatline in region (minutes)',
    'flatlined_percent_REGION': 'Percentage of flatlined data in region',
    'imputed_duration_REGION': 'Duration of imputed data in region (hours)',
    'imputed_percent_REGION': 'Percentage of imputed data in region',
    'imputed_low_quality_duration_REGION': 'Duration of imputed low quality data in region (hours)',
    'imputed_low_quality_percent_REGION': 'Percentage of imputed low quality data in region',
    'unimputed_low_quality_duration_REGION': 'Duration of low quality data not imputed in region (hours)',
    'unimputed_low_quality_percent_REGION': 'Percentage of low quality data not imputed in region',
    'total_low_quality_duration_REGION': 'Total duration of low quality data in region (hours)',
    'total_low_quality_percent_REGION': 'Total percentage of low quality data in region',
    'imputed_gap_duration_REGION': 'Duration of imputed gap data in region (hours)',
    'imputed_gap_percent_REGION': 'Percentage of imputed gap data in region',
    'unimputed_gap_duration_REGION': 'Duration of unimputed gap data in region (hours)',
    'unimputed_gap_percent_REGION': 'Percentage of unimputed gap data in region',
    'total_gap_duration_REGION': 'Total duration of gap data in region (hours)',
    'total_gap_percent_REGION': 'Total percentage of gap data in region',
    'gap_imputation_ratio_REGION': 'Ratio of gap data that has been imputed in region (0-1)',
    'imputed_non_wear_duration_REGION': 'Duration of imputed non-wear data in region (hours)',
    'imputed_non_wear_percent_REGION': 'Percentage of imputed non-wear data in region',
    'unimputed_non_wear_duration_REGION': 'Duration of unimputed non-wear data in region (hours)',
    'unimputed_non_wear_percent_REGION': 'Percentage of unimputed non-wear data in region',
    'total_non_wear_duration_REGION': 'Total duration of non-wear data in region (hours)',
    'total_non_wear_percent_REGION': 'Total percentage of non-wear data in region',
    'non_wear_imputation_ratio_REGION': 'Ratio of non-wear data that has been imputed in region (0-1)',
    'imputed_jump_duration_REGION': 'Duration of imputed jump data in region (hours)',
    'imputed_jump_percent_REGION': 'Percentage of imputed jump data in region',
    'unimputed_jump_duration_REGION': 'Duration of unimputed jump data in region (hours)',
    'unimputed_jump_percent_REGION': 'Percentage of unimputed jump data in region',
    'total_jump_duration_REGION': 'Total duration of jump data in region (hours)',
    'total_jump_percent_REGION': 'Total percentage of jump data in region',
    'jump_imputation_ratio_REGION': 'Ratio of jump data that has been imputed in region (0-1)',
    'imputed_plummet_duration_REGION': 'Duration of imputed plummet data in region (hours)',
    'imputed_plummet_percent_REGION': 'Percentage of imputed plummet data in region',
    'unimputed_plummet_duration_REGION': 'Duration of unimputed plummet data in region (hours)',
    'unimputed_plummet_percent_REGION': 'Percentage of unimputed plummet data in region',
    'total_plummet_duration_REGION': 'Total duration of plummet data in region (hours)',
    'total_plummet_percent_REGION': 'Total percentage of plummet data in region',
    'plummet_imputation_ratio_REGION': 'Ratio of plummet data that has been imputed in region (0-1)',
    'imputed_extreme_negative_duration_REGION': 'Duration of imputed extreme negative data in region (hours)',
    'imputed_extreme_negative_percent_REGION': 'Percentage of imputed extreme negative data in region',
    'unimputed_extreme_negative_duration_REGION': 'Duration of unimputed extreme negative data in region (hours)',
    'unimputed_extreme_negative_percent_REGION': 'Percentage of unimputed extreme negative data in region',
    'total_extreme_negative_duration_REGION': 'Total duration of extreme negative data in region (hours)',
    'total_extreme_negative_percent_REGION': 'Total percentage of extreme negative data in region',
    'sub_negative_10_sum_REGION': 'Sum of all TAC values below -10 in region',
    'extreme_negative_imputation_ratio_REGION': 'Ratio of extreme negative data that has been imputed in region (0-1)',
    'low_quality_imputation_ratio_REGION': 'Ratio of overall low quality data that has been imputed in region (0-1)',
    'total_gaps_and_non_wear_percent_REGION': 'Total percentage of gaps and non-wear combined in region'
  },
  
  'Curve Flags': {
    'FLAG_unimputed_low_quality_curve': 'Flag for unimputed low quality data in the curve',
    'FLAG_imputed_limit_curve': 'Flag for imputed data exceeding limits in the curve',
    'FLAG_unimputed_jump_curve': 'Flag for unimputed jump data in the curve',
    'FLAG_below_threshold_curve': 'Flag for curve values below the detection threshold',
    'FLAG_flat_curve': 'Flag for flatlined curve data',
    'FLAG_incomplete_curve_start_curve': 'Flag for incomplete curve start (short rise duration)',
    'FLAG_incomplete_periphery_before_curve': 'Flag for insufficient periphery before the curve (< 30 minutes)',
    'FLAG_imputed_rise_curve': 'Flag for imputed data during the rise phase of the curve',
    'FLAG_incomplete_curve_end_curve': 'Flag for incomplete curve end (short fall duration)',
    'FLAG_incomplete_periphery_after_curve': 'Flag for insufficient periphery after the curve (< 30 minutes)',
    'FLAG_imputed_fall_curve': 'Flag for imputed data during the fall phase of the curve'
  },
  
  'Periphery Flags': {
    'FLAG_gaps_and_non_wear_periphery_before': 'Flag for gaps and non-wear in the periphery before the curve',
    'FLAG_extreme_negative_periphery_before': 'Flag for extreme negative values in the periphery before the curve',
    'FLAG_unimputed_low_quality_periphery_before': 'Flag for unimputed low quality data in the periphery before the curve',
    'FLAG_gaps_and_non_wear_periphery_after': 'Flag for gaps and non-wear in the periphery after the curve',
    'FLAG_extreme_negative_periphery_after': 'Flag for extreme negative values in the periphery after the curve',
    'FLAG_unimputed_low_quality_periphery_after': 'Flag for unimputed low quality data in the periphery after the curve'
  },
  
  'Validity Indicators': {
    'CURVE_VALID': 'Whether the curve meets validity criteria (1=valid, 0=invalid)',
    'PERIPHERY_VALID': 'Whether the periphery meets validity criteria (1=valid, 0=invalid)',
    'REGION_VALID': 'Whether the overall region (curve + periphery) meets validity criteria (1=valid, 0=invalid)'
  },
  
  'Baseline Information': {
    'unadjusted_threshold': 'Unadjusted threshold value used for curve detection',
    'baseline_mean': 'Mean baseline TAC value',
    'baseline_sd': 'Standard deviation of baseline TAC values'
  },
  
  'Visualization Paths': {
    'smoothed_curve_plot': 'Path to smoothed curve plot (None for web API)',
    'signal_processing_plot': 'Path to signal processing plot (None for web API)',
    'device_removal_plot': 'Path to device removal plot (None for web API)',
    'signal_processing_plot_wide': 'Path to wide signal processing plot (None for web API)'
  }
};

/**
 * Generates a variable key sheet for curve features using XLSX format
 * Organized by category with section headers, matching backend report_guide.py structure
 * 
 * @param {Array} curveFeatures - Array of curve feature objects
 * @returns {Object} XLSX worksheet object
 */
export function generateVariableKeySheet(curveFeatures = []) {
  const sheetData = [];
  
  // Collect all column names from actual data
  const actualColumns = new Set();
  if (curveFeatures && curveFeatures.length > 0) {
    curveFeatures.forEach(curve => {
      Object.keys(curve).forEach(col => actualColumns.add(col));
    });
  }
  
  // Iterate through each category
  Object.entries(featureCategories).forEach(([categoryName, features], categoryIndex) => {
    // Add category header
    sheetData.push([categoryName, '']);
    sheetData.push(['Variable', 'Description']);
    
    // Add features in this category that exist in the data
    Object.entries(features).forEach(([variable, description]) => {
      if (actualColumns.has(variable)) {
        sheetData.push([variable, description]);
      }
    });
    
    // Add spacing between categories (3 empty rows)
    sheetData.push(['', '']);
    sheetData.push(['', '']);
    sheetData.push(['', '']);
  });
  
  // Add any columns that weren't in our predefined categories
  const undocumentedColumns = Array.from(actualColumns).filter(col => {
    return !Object.values(featureCategories).some(category => col in category);
  }).sort();
  
  if (undocumentedColumns.length > 0) {
    sheetData.push(['Other Variables', '']);
    sheetData.push(['Variable', 'Description']);
    undocumentedColumns.forEach(col => {
      sheetData.push([col, 'See region definitions for suffix meanings']);
    });
  }
  
  // Convert to XLSX worksheet
  const worksheet = XLSX.utils.aoa_to_sheet(sheetData);
  
  // Set column widths
  worksheet['!cols'] = [
    { wch: 50 },  // Variable column
    { wch: 100 }  // Description column
  ];
  
  return worksheet;
}

// Export the feature categories for potential reuse
export { featureCategories };
